import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subscription, take } from 'rxjs';

import { LogisticService, ORDER_BLS_LIMIT, SERVER_DEFAULT_LIMIT } from '@app/logistic/logistic.service';
import { ValuesService } from '@app/common/values/values.service';
import { TzDatePipe } from '@app/shared/tz-date.pipe';
import { C_value } from '@app/shared/_models/common';
import { C_VALUES_DELIVERY_SLOT } from '@app/shared/constants';
import { todayStr } from '@app/shared/time';
import { frDay } from '@app/sales/rental-billing/rental-days';
import { serverMessage } from '@app/sales/rental-billing/rental-billing.service';
import { boardOffTip, signedOffTip } from '@app/sales/rental-billing/rental-line-docs';
import { BlIn, BlLineIn, RentalBl, SIGNED_DAYS, mayMiss, rentalBlsOf } from './rental-bls.model';

// Plafonds possibles de la lecture : celui demandé, ou 100 quand le serveur le saute (#57).
const READ_CAPS = [SERVER_DEFAULT_LIMIT, ORDER_BLS_LIMIT];

// BL du dossier de location : non signés et signés récents, avec leurs liens (planification, BL signé, board des tournées) ; repliée à l'arrivée.
@Component({
  selector: 'app-rental-bls',
  imports: [NgTemplateOutlet, RouterLink, MatIcon, MatProgressSpinner, MatTooltipModule, TzDatePipe],
  templateUrl: './rental-bls.component.html',
  styleUrl: './rental-bls.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class RentalBlsComponent implements OnInit, OnChanges {
  @Input({ required: true }) orderId!: number;
  @Input() lines: BlLineIn[] = [];                 // lignes lues par la page : patient et nature de chaque ligne de BL
  @Input() reload$: Observable<void> | null = null; // relecture de la pièce : les BL sont relus avec elle
  @Output() openBl = new EventEmitter<number>();

  private logisticService = inject(LogisticService);
  private valuesService = inject(ValuesService);
  private destroyRef = inject(DestroyRef);

  readonly signedDays = SIGNED_DAYS;
  readonly frDay = frDay;
  folded = true;
  dels: BlIn[] | null = null;      // null avant la première lecture de ce dossier
  loading = false;
  failed: string | null = null;
  open: RentalBl[] = [];
  signed: RentalBl[] = [];
  private slots: C_value[] = [];
  private readSub?: Subscription;

  ngOnInit(): void {
    this.valuesService.getValues$(C_VALUES_DELIVERY_SLOT).pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (s: C_value[]) => { this.slots = s ?? []; this.compute(); }, error: () => this.slots = [] });   // sans eux : lien sur la journée entière
    if (this.reload$) this.reload$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.read());
    else this.read();
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['orderId'] && !ch['orderId'].firstChange) { this.dels = null; this.compute(); }
    if (ch['lines']) this.compute();
  }

  // Les BL déjà affichés restent à l'écran pendant la relecture.
  read(): void {
    if (!this.orderId) return;
    const id = this.orderId;
    this.readSub?.unsubscribe();
    this.loading = true;
    this.readSub = this.logisticService.getDeliveriesOfOrder$(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (dels) => { if (id !== this.orderId) return; this.loading = false; this.failed = null; this.dels = dels ?? []; this.compute(); },
      error: (e) => { this.loading = false; this.failed = serverMessage(e); console.error('[RentalBls] lecture des BL', e); },
    });
  }

  private compute(): void {
    const v = rentalBlsOf(this.dels ?? [], this.lines ?? [], this.slots, todayStr());
    this.open = v.open;
    this.signed = v.signed;
    this.truncated = mayMiss(this.dels ?? [], todayStr(), READ_CAPS);
  }

  truncated = false;

  halfLabel(b: RentalBl): string { return b.half === 1 ? 'matin' : b.half === 2 ? 'après-midi' : ''; }

  runLabel(b: RentalBl): string {
    if (b.run) return b.stop ? `tournée, arrêt ${b.stop}` : 'dans une tournée';
    return b.planning ? (b.signed ? 'hors tournée' : 'pas encore dans une tournée') : b.modeLabel;
  }

  get countLabel(): string {
    const n = this.open.length, s = this.signed.length;
    return `${n} planifié${n > 1 ? 's' : ''} · ${s} signé${s > 1 ? 's' : ''} depuis ${SIGNED_DAYS} jours`;
  }

  signedOffTip(b: RentalBl): string { return signedOffTip(b.signedAt); }
  boardOffTip(b: RentalBl): string { return boardOffTip(b.mode, b.day); }
}
